import {
  BlockConfig,
  zeroIOPS,
  OperationTypes,
  ContentTypes,
  Block,
  IOPS,
  PASSTHROUGH_INFINITE_CAPACITY,
} from "../block-base";
import { BlockTypes } from "../block-types";

export const passthroughSideEffects = (block: Block, iops: IOPS): Block => {
  let finalBlock = { ...block };
  let finalDynamicProperties = finalBlock.dynamicProperties || {};

  // Check if block has children via dynamicProperties (set by game loop)
  // Default: no children → act as a sink with infinite processing
  const hasChildren = finalDynamicProperties["hasChildren"] === true;

  if (hasChildren) {
    // Has children → zero processing, just forward everything
    finalBlock.processingCapacity = zeroIOPS();
  } else {
    // No children → process everything (act as infinite sink)
    finalBlock.processingCapacity = {
      total: PASSTHROUGH_INFINITE_CAPACITY,
      byOperationType: {
        [`${OperationTypes.READ}`]: {
          total: PASSTHROUGH_INFINITE_CAPACITY,
          byContentType: {
            [`${ContentTypes.DYNAMIC}`]: PASSTHROUGH_INFINITE_CAPACITY,
            [`${ContentTypes.STATIC}`]: PASSTHROUGH_INFINITE_CAPACITY,
          },
        },
        [`${OperationTypes.WRITE}`]: {
          total: PASSTHROUGH_INFINITE_CAPACITY,
          byContentType: { [`${ContentTypes.DYNAMIC}`]: PASSTHROUGH_INFINITE_CAPACITY },
        },
      },
    };
  }

  finalBlock.dynamicProperties = finalDynamicProperties;
  return finalBlock;
};

export const passthroughConfig: BlockConfig = {
  displayName: "Passthrough",
  imagePath: "",
  isInitiator: true,
  isTerminator: true,
  isInterceptor: false,
  allowedInputTypes: new Set([
    BlockTypes.USER,
    BlockTypes.APP_SERVER,
    BlockTypes.AUTO_SCALING_GROUP,
    BlockTypes.LOAD_BALANCER,
    BlockTypes.NETWORK_LOAD_BALANCER,
    BlockTypes.LAMBDA,
    BlockTypes.QUEUE,
    BlockTypes.CDN,
    BlockTypes.PASSTHROUGH,
    BlockTypes.GRAPH_DB,
    BlockTypes.DOCUMENT_DB,
    BlockTypes.S3_BUCKET,
    BlockTypes.DB_READ_REPLICA,
  ]),
  allowedOutputTypes: new Set([
    BlockTypes.APP_SERVER,
    BlockTypes.AUTO_SCALING_GROUP,
    BlockTypes.DATABASE,
    BlockTypes.DB_READ_REPLICA,
    BlockTypes.LOAD_BALANCER,
    BlockTypes.NETWORK_LOAD_BALANCER,
    BlockTypes.LAMBDA,
    BlockTypes.QUEUE,
    BlockTypes.CACHE,
    BlockTypes.CDN,
    BlockTypes.GRAPH_DB,
    BlockTypes.DOCUMENT_DB,
    BlockTypes.S3_BUCKET,
    BlockTypes.PASSTHROUGH,
  ]),
  inputCapacity: {
    total: PASSTHROUGH_INFINITE_CAPACITY,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: PASSTHROUGH_INFINITE_CAPACITY,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: PASSTHROUGH_INFINITE_CAPACITY,
          [`${ContentTypes.STATIC}`]: PASSTHROUGH_INFINITE_CAPACITY,
          [`${ContentTypes.STATIC_MEDIA}`]: PASSTHROUGH_INFINITE_CAPACITY,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: PASSTHROUGH_INFINITE_CAPACITY,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: PASSTHROUGH_INFINITE_CAPACITY,
          [`${ContentTypes.STATIC_MEDIA}`]: PASSTHROUGH_INFINITE_CAPACITY,
        },
      },
    },
  },
  processingCapacity: zeroIOPS(), // Will be set dynamically by side effects
  purchasePrice: 50,
  sellPrice: 25,
  costPerCycle: 0.1,
  outputConnectorLimits: {},
  blockSideEffects: (block: Block, iops: IOPS) => passthroughSideEffects(block, iops),
};
